// When a recording starts and how the page knows it (PRD-0074, slice 06): the caption above the
// player. One line in words — the time, then where it came from, then what else was read — and,
// for a collaborator, the one control that sets the operator's own time or hands the start back
// to the analysis. Pure: the time and the write arrive as props.
import { Clock3 } from 'lucide-react'
import { type FormEvent, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { RecordedAtEdit } from '@/features/document-overlay/hooks/useDocumentVideo'
import {
  hasAnalysisTime,
  otherReadings,
  type RecordedTime,
} from '@/features/document-overlay/recording-time'
import { cn } from '@/utils/cn'
import { formatExactDateTime, fromLocalInputValue, toLocalInputValue } from '@/utils/date'

const COPY = 'documents.detail.video.recording'

export interface RecordingTimeProps {
  recording: RecordedTime
  /** The control that sets the operator's time; absent for a viewer. */
  edit?: RecordedAtEdit | null
  className?: string
}

export function RecordingTime({ recording, edit = null, className }: RecordingTimeProps) {
  const { t, i18n } = useTranslation()
  const when = (iso: string) => formatExactDateTime(iso, i18n.language)
  const others = otherReadings(recording)
  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 text-sm', className)}
      data-testid="recording-time"
      data-source={recording.source ?? 'none'}
    >
      <Clock3 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      {recording.time ? (
        <span className="text-foreground">
          {t(`${COPY}.recorded`)}{' '}
          <time dateTime={recording.time} className="font-mono tabular-nums">
            {when(recording.time)}
          </time>
        </span>
      ) : (
        <span className="text-foreground">{t(`${COPY}.unknown`)}</span>
      )}
      <span className="text-muted-foreground">
        {recording.time && recording.source
          ? t(`${COPY}.source.${recording.source}`)
          : t(`${COPY}.unknownHint`)}
        {others.map(reading => (
          <span key={reading.source}>
            {' · '}
            {t(`${COPY}.also.${reading.source}`, { time: when(reading.time) })}
          </span>
        ))}
      </span>
      {edit ? <SetRecordingTime recording={recording} edit={edit} /> : null}
    </div>
  )
}

/** The control: "Change" (or "Set the time") opens the form in a popover; a save closes it. */
function SetRecordingTime({ recording, edit }: { recording: RecordedTime; edit: RecordedAtEdit }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const submit = async (recordedAt: string | null) => {
    if (await edit.save(recordedAt)) setOpen(false)
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="xs" className="ml-auto" data-testid="recording-time-change">
          {t(recording.time ? `${COPY}.change` : `${COPY}.set`)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 gap-3 p-3">
        {/* Mounted on every open: the form seeds itself from the time shown. */}
        <RecordingTimeForm
          recording={recording}
          edit={edit}
          onSubmit={submit}
          onCancel={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}

interface RecordingTimeFormProps {
  recording: RecordedTime
  edit: RecordedAtEdit
  /** The time to save (ISO 8601), or null to hand the start back to the analysis. */
  onSubmit: (recordedAt: string | null) => void
  onCancel: () => void
}

/** The start to the second, Save, and the way back to what the analysis found. */
function RecordingTimeForm({ recording, edit, onSubmit, onCancel }: RecordingTimeFormProps) {
  const { t } = useTranslation()
  const inputId = useId()
  const [value, setValue] = useState(() => toLocalInputValue(recording.time ?? '', true))
  const save = (event: FormEvent) => {
    event.preventDefault()
    const iso = fromLocalInputValue(value)
    if (iso) onSubmit(iso)
  }
  return (
    <form onSubmit={save} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor={inputId}>{t(`${COPY}.editTitle`)}</Label>
        <p className="text-xs text-muted-foreground">{t(`${COPY}.editHint`)}</p>
      </div>
      <Input
        id={inputId}
        type="datetime-local"
        step={1}
        value={value}
        onChange={event => setValue(event.target.value)}
        required
        aria-invalid={edit.error ? true : undefined}
        data-testid="recording-time-input"
      />
      {edit.error ? (
        <p className="text-xs text-destructive" role="alert">
          {t(`${COPY}.error.${edit.error}`)}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {recording.source === 'operator' ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="mr-auto"
            disabled={edit.isSaving}
            onClick={() => onSubmit(null)}
            data-testid="recording-time-clear"
          >
            {t(hasAnalysisTime(recording.candidates) ? `${COPY}.useAnalysis` : `${COPY}.remove`)}
          </Button>
        ) : null}
        <Button type="button" variant="ghost" size="xs" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" size="xs" disabled={edit.isSaving || !value}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}
